/**
 * Community Landowners Association - Parcel Billing Data
 *
 * Represents the 406 individual property parcels that pay annual dues
 * to the Community Landowners Association.
 */

export interface ParcelBilling {
  id: string;
  parcelNumber: string;
  ownerName: string;
  ownerEmail: string;
  annualDues: number;
  nextBillingDate: string;
  lastPaymentDate?: string;
  paymentStatus: 'paid' | 'pending' | 'overdue' | 'payment_plan';
  segment: 'north' | 'south' | 'gav';
  propertyType: 'residential' | 'commercial' | 'vacant';
  votingRights: boolean;
  notes?: string;
}

// First names pool
const firstNames = [
  'James',
  'Mary',
  'John',
  'Patricia',
  'Robert',
  'Jennifer',
  'Michael',
  'Linda',
  'William',
  'Barbara',
  'David',
  'Elizabeth',
  'Richard',
  'Susan',
  'Joseph',
  'Jessica',
  'Thomas',
  'Sarah',
  'Charles',
  'Karen',
  'Christopher',
  'Nancy',
  'Daniel',
  'Lisa',
  'Matthew',
  'Betty',
  'Anthony',
  'Margaret',
  'Mark',
  'Sandra',
  'Donald',
  'Ashley',
  'Steven',
  'Kimberly',
  'Paul',
  'Emily',
  'Andrew',
  'Donna',
  'Joshua',
  'Michelle',
  'Kenneth',
  'Dorothy',
  'Kevin',
  'Carol',
  'Brian',
  'Amanda',
  'George',
  'Melissa',
  'Edward',
  'Deborah',
  'Ronald',
  'Stephanie',
  'Timothy',
  'Rebecca',
  'Jason',
  'Sharon',
  'Jeffrey',
  'Laura',
  'Ryan',
  'Cynthia',
  'Jacob',
  'Kathleen',
  'Gary',
  'Amy',
  'Nicholas',
  'Shirley',
  'Eric',
  'Angela',
  'Jonathan',
  'Helen',
  'Stephen',
  'Anna',
  'Larry',
  'Brenda',
  'Justin',
  'Pamela',
  'Scott',
  'Nicole',
  'Brandon',
  'Emma',
  'Benjamin',
  'Samantha',
  'Samuel',
  'Katherine',
  'Raymond',
  'Christine',
  'Gregory',
  'Debra',
  'Frank',
  'Rachel',
  'Alexander',
  'Catherine',
  'Patrick',
  'Carolyn',
  'Raymond',
  'Janet',
  'Jack',
  'Ruth',
  'Dennis',
  'Maria',
  'Jerry',
  'Heather',
  'Tyler',
  'Diane',
  'Aaron',
  'Virginia',
  'Jose',
  'Julie',
  'Adam',
  'Joyce',
  'Henry',
  'Victoria',
  'Nathan',
  'Olivia',
  'Douglas',
  'Kelly',
  'Zachary',
  'Christina',
  'Peter',
  'Lauren',
  'Kyle',
  'Joan',
  'Walter',
  'Evelyn',
  'Ethan',
  'Judith',
  'Jeremy',
  'Megan',
  'Harold',
  'Cheryl',
  'Keith',
  'Andrea',
  'Christian',
  'Hannah',
  'Roger',
  'Martha',
  'Noah',
  'Jacqueline',
  'Gerald',
  'Frances',
  'Carl',
  'Gloria',
  'Terry',
  'Ann',
  'Sean',
  'Teresa',
  'Austin',
  'Kathryn',
  'Arthur',
  'Sara',
  'Lawrence',
  'Janice',
  'Jesse',
  'Jean',
  'Dylan',
  'Alice',
  'Bryan',
  'Madison',
  'Joe',
  'Doris',
];

// Last names pool
const lastNames = [
  'Smith',
  'Johnson',
  'Williams',
  'Brown',
  'Jones',
  'Garcia',
  'Miller',
  'Davis',
  'Rodriguez',
  'Martinez',
  'Hernandez',
  'Lopez',
  'Gonzalez',
  'Wilson',
  'Anderson',
  'Thomas',
  'Taylor',
  'Moore',
  'Jackson',
  'Martin',
  'Lee',
  'Perez',
  'Thompson',
  'White',
  'Harris',
  'Sanchez',
  'Clark',
  'Ramirez',
  'Lewis',
  'Robinson',
  'Walker',
  'Young',
  'Allen',
  'King',
  'Wright',
  'Scott',
  'Torres',
  'Nguyen',
  'Hill',
  'Flores',
  'Green',
  'Adams',
  'Nelson',
  'Baker',
  'Hall',
  'Rivera',
  'Campbell',
  'Mitchell',
  'Carter',
  'Roberts',
  'Gomez',
  'Phillips',
  'Evans',
  'Turner',
  'Diaz',
  'Parker',
  'Cruz',
  'Edwards',
  'Collins',
  'Reyes',
  'Stewart',
  'Morris',
  'Morales',
  'Murphy',
  'Cook',
  'Rogers',
  'Gutierrez',
  'Ortiz',
  'Morgan',
  'Cooper',
  'Peterson',
  'Bailey',
  'Reed',
  'Kelly',
  'Howard',
  'Ramos',
  'Kim',
  'Cox',
  'Ward',
  'Richardson',
  'Watson',
  'Brooks',
  'Chavez',
  'Wood',
  'James',
  'Bennett',
  'Gray',
  'Mendoza',
  'Ruiz',
  'Hughes',
  'Price',
  'Alvarez',
  'Castillo',
  'Sanders',
  'Patel',
  'Myers',
  'Long',
  'Ross',
  'Foster',
  'Jimenez',
  'Powell',
  'Jenkins',
  'Perry',
  'Russell',
  'Sullivan',
  'Bell',
  'Coleman',
  'Butler',
  'Henderson',
  'Barnes',
  'Gonzales',
  'Fisher',
  'Vasquez',
  'Simmons',
  'Romero',
  'Jordan',
  'Patterson',
  'Alexander',
  'Hamilton',
  'Graham',
  'Reynolds',
  'Griffin',
  'Wallace',
  'Moreno',
  'West',
  'Cole',
  'Hayes',
  'Bryant',
  'Herrera',
  'Gibson',
  'Ellis',
  'Tran',
  'Medina',
  'Aguilar',
  'Stevens',
  'Murray',
  'Ford',
  'Castro',
  'Marshall',
  'Owens',
  'Harrison',
  'Fernandez',
  'McDonald',
  'Woods',
  'Washington',
  'Kennedy',
  'Wells',
  'Vargas',
  'Henry',
  'Chen',
  'Freeman',
  'Webb',
  'Tucker',
  'Guzman',
  'Burns',
  'Crawford',
  'Olson',
  'Simpson',
  'Porter',
  'Hunter',
  'Gordon',
  'Mendez',
  'Silva',
  'Shaw',
  'Snyder',
  'Mason',
  'Dixon',
  'Munoz',
];

// Deterministically pick a placeholder name for a parcel index.
// Coprime strides keep first/last name pairings varied without randomness.
function generateName(index: number): string {
  const firstName = firstNames[(index * 7) % firstNames.length];
  const lastName = lastNames[(index * 13) % lastNames.length];
  return `${firstName} ${lastName}`;
}

// Generate email from name
function generateEmail(name: string): string {
  const parts = name.toLowerCase().split(' ');
  return `${parts[0]}.${parts[1]}@example.com`;
}

// Generate parcel number (e.g., GLA-N-001, GLA-S-123, GLA-GAV-12)
function generateParcelNumber(index: number, segment: 'north' | 'south' | 'gav'): string {
  if (segment === 'gav') {
    return `GLA-GAV-${String(index).padStart(2, '0')}`;
  } else if (segment === 'north') {
    return `GLA-N-${String(index).padStart(3, '0')}`;
  } else {
    return `GLA-S-${String(index).padStart(3, '0')}`;
  }
}

// Generate billing date (most are in March, some in April for late payments)
function generateNextBillingDate(status: string): string {
  if (status === 'overdue' || status === 'payment_plan') {
    // Overdue payments - already passed
    return '2026-03-31';
  }
  // Next cycle
  return '2026-03-31';
}

// Generate last payment date
function generateLastPaymentDate(status: string, index: number): string | undefined {
  if (status === 'paid') {
    const dates = [
      '2025-03-15',
      '2025-03-18',
      '2025-03-21',
      '2025-03-24',
      '2025-03-27',
      '2025-03-10',
      '2025-03-12',
      '2025-03-14',
      '2025-03-16',
      '2025-03-19',
      '2025-03-22',
      '2025-03-25',
      '2025-03-28',
      '2025-03-30',
    ];
    return dates[index % dates.length];
  } else if (status === 'payment_plan') {
    return '2025-06-15';
  }
  return undefined;
}

// Deterministically assign a payment status from the parcel's position in its
// segment. Every parcel keeps the same status on every render.
function assignPaymentStatus(
  index: number,
  segment: 'north' | 'south' | 'gav',
): 'paid' | 'pending' | 'overdue' | 'payment_plan' {
  const slot = index % 100;
  if (segment === 'gav') {
    if (slot < 95) return 'paid';
    if (slot < 98) return 'pending';
    return 'overdue';
  }
  if (slot < 92) return 'paid';
  if (slot < 97) return 'pending';
  if (slot < 99) return 'overdue';
  return 'payment_plan';
}

/**
 * SAMPLE DATA - NOT REAL PARCEL RECORDS.
 *
 * Placeholder owner names, emails and payment statuses generated for
 * demonstration. No figure here reflects any actual parcel, owner, or
 * account standing. Only the parcel counts and the annual dues amount
 * correspond to the real association.
 *
 * Segment counts:
 * - North Glastonbury: 214 parcels
 * - South Glastonbury: 168 parcels
 * - Golden Age Village: 24 parcels
 * Total: 406 parcels
 *
 * Payment statuses are assigned deterministically by parcel index so the
 * same parcel always shows the same status. They are illustrative only.
 */
export function generateGlastonburyParcels(): ParcelBilling[] {
  const parcels: ParcelBilling[] = [];
  let parcelIndex = 1;

  // North Glastonbury - 214 parcels (53%)
  for (let i = 1; i <= 214; i++) {
    const status = assignPaymentStatus(i, 'north');
    const name = generateName(i);

    parcels.push({
      id: `parcel-north-${i}`,
      parcelNumber: generateParcelNumber(i, 'north'),
      ownerName: name,
      ownerEmail: generateEmail(name),
      annualDues: 494.4,
      nextBillingDate: generateNextBillingDate(status),
      lastPaymentDate: generateLastPaymentDate(status, i),
      paymentStatus: status,
      segment: 'north',
      propertyType: i % 20 === 0 ? 'commercial' : i % 40 === 0 ? 'vacant' : 'residential',
      votingRights: true,
      notes:
        status === 'payment_plan'
          ? 'On approved payment plan'
          : status === 'overdue'
            ? 'Late payment notice sent'
            : undefined,
    });
  }

  // South Glastonbury - 168 parcels (41%)
  for (let i = 1; i <= 168; i++) {
    const status = assignPaymentStatus(i, 'south');
    const name = generateName(i + 214);

    parcels.push({
      id: `parcel-south-${i}`,
      parcelNumber: generateParcelNumber(i, 'south'),
      ownerName: name,
      ownerEmail: generateEmail(name),
      annualDues: 494.4,
      nextBillingDate: generateNextBillingDate(status),
      lastPaymentDate: generateLastPaymentDate(status, i),
      paymentStatus: status,
      segment: 'south',
      propertyType: i % 20 === 0 ? 'commercial' : i % 40 === 0 ? 'vacant' : 'residential',
      votingRights: true,
    });
  }

  // Golden Age Village - 24 parcels (6%)
  for (let i = 1; i <= 24; i++) {
    const status = assignPaymentStatus(i, 'gav');
    const name = generateName(i + 382);

    parcels.push({
      id: `parcel-gav-${i}`,
      parcelNumber: generateParcelNumber(i, 'gav'),
      ownerName: name,
      ownerEmail: generateEmail(name),
      annualDues: 494.4,
      nextBillingDate: generateNextBillingDate(status),
      lastPaymentDate: generateLastPaymentDate(status, i),
      paymentStatus: status,
      segment: 'gav',
      propertyType: 'residential',
      votingRights: true,
      notes: 'Golden Age Village Association member',
    });
  }

  return parcels;
}

// Generate and export the parcel data
export const glastonburyParcels = generateGlastonburyParcels();

// Export statistics
export const parcelStatistics = {
  total: glastonburyParcels.length,
  bySegment: {
    north: glastonburyParcels.filter((p) => p.segment === 'north').length,
    south: glastonburyParcels.filter((p) => p.segment === 'south').length,
    gav: glastonburyParcels.filter((p) => p.segment === 'gav').length,
  },
  byPaymentStatus: {
    paid: glastonburyParcels.filter((p) => p.paymentStatus === 'paid').length,
    pending: glastonburyParcels.filter((p) => p.paymentStatus === 'pending').length,
    overdue: glastonburyParcels.filter((p) => p.paymentStatus === 'overdue').length,
    payment_plan: glastonburyParcels.filter((p) => p.paymentStatus === 'payment_plan').length,
  },
  totalAnnualRevenue: glastonburyParcels.reduce((sum, p) => sum + p.annualDues, 0),
  collectedRevenue: glastonburyParcels
    .filter((p) => p.paymentStatus === 'paid')
    .reduce((sum, p) => sum + p.annualDues, 0),
  outstandingRevenue: glastonburyParcels
    .filter((p) => p.paymentStatus !== 'paid')
    .reduce((sum, p) => sum + p.annualDues, 0),
};
